import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Heart, MessageCircle, Eye, Send, ArrowLeft, ArrowRight, CornerDownRight, Trash2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import '../styles/BookReader.css';
import getProfileImageUrl from '../utils/getProfileImageUrl';

const BookReader = () => {
    const { bookId, chapterId } = useParams();
    const navigate = useNavigate();
    const [book, setBook] = useState(null);
    const [currentChapter, setCurrentChapter] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [replyTo, setReplyTo] = useState(null);
    const [showChapters, setShowChapters] = useState(false);
    const [likedChapters, setLikedChapters] = useState({});
    const [views, setViews] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState(null);
    const currentUserId = parseInt(localStorage.getItem('userId'), 10);

    const fetchBookDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }
            const response = await api.get(`/bookReader/books/${bookId}/${chapterId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const data = response.data;
            const authorName = data.chapters[0]?.author_name || 'Bilinmeyen Yazar';
            const publishedChapters = data.chapters.filter(chap => chap.is_published);
            const chapter = publishedChapters.find(chap => chap.chapter_id === parseInt(chapterId, 10));

            const initialViews = chapter?.views || 0;

            const updatedLikedChapters = {};
            publishedChapters.forEach(chap => {
                updatedLikedChapters[chap.chapter_id] = chap.is_liked;
            });
            setBook({ ...data, author_name: authorName, chapters: publishedChapters });
            setCurrentChapter(chapter);
            setLikedChapters(updatedLikedChapters);

            setViews(Number.isNaN(initialViews) ? 0 : initialViews);

            await incrementViewCount();

            setLoading(false);
        } catch (error) {
            console.error('Error fetching book details:', error);
            setLoading(false);
        }
    };

    const incrementViewCount = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await api.post(`/bookReader/books/${bookId}/chapters/${chapterId}/incrementViews`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.data.viewUpdated) {
                setViews(prevViews => prevViews + 1);
            }

        } catch (error) {
            console.error('Error incrementing view count:', error);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchBookDetails();
    }, [chapterId]);

    useEffect(() => {
        const fetchComments = async () => {
            if (!currentChapter) return;
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    navigate('/login');
                    return;
                }
                const response = await api.get(`/bookReader/chapters/${currentChapter.chapter_id}/comments`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setComments(response.data);
            } catch (error) {
                console.error('Error fetching comments:', error);
            }
        };
        fetchComments();
    }, [currentChapter]);

    const toggleChapters = () => {
        setShowChapters(!showChapters);
    };

    const handleLike = async () => {
        if (!currentChapter) return;
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }
            const response = await api.post(`/bookReader/chapters/${currentChapter.chapter_id}/like`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const isLiked = response.data.isLiked;
            const updatedLikes = isLiked
                ? currentChapter.likes + 1
                : currentChapter.likes - 1;
            setCurrentChapter((prev) => ({
                ...prev,
                likes: updatedLikes,
            }));
            setLikedChapters((prev) => ({
                ...prev,
                [currentChapter.chapter_id]: isLiked,
            }));
        } catch (error) {
            console.error('Error liking the chapter:', error);
        }
    };

    const addComment = async (content, parentId) => {
        if (!content.trim()) return;
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        try {
            await api.post(`/bookReader/books/${bookId}/chapters/${currentChapter.chapter_id}/comments`, {
                content: content,
                parentCommentId: parentId,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setNewComment('');
            setReplyTo(null);
            const response = await api.get(`/bookReader/chapters/${currentChapter.chapter_id}/comments`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setComments(response.data);
        } catch (error) {
            console.error('Error adding comment:', error);
        }
    };

    const openDeleteModal = (commentId) => {
        setCommentToDelete(commentId);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setCommentToDelete(null);
        setShowDeleteModal(false);
    };

    const handleConfirmDelete = async () => {
        if (!commentToDelete) return;

        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            await api.delete(`/bookReader/comments/${commentToDelete}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            // Yorumu ve tüm alt yanıtlarını yerel state'den kaldır
            const removeCommentAndChildren = (id, commentList) => {
                const directReplies = commentList.filter(c => c.parent_comment_id === id);
                let idsToRemove = [id, ...directReplies.flatMap(reply => removeCommentAndChildren(reply.comment_id, commentList))];
                return idsToRemove.flat();
            };

            const allIdsToRemove = removeCommentAndChildren(commentToDelete, comments);
            setComments(prev => prev.filter(c => !allIdsToRemove.includes(c.comment_id)));
            closeDeleteModal();
        } catch (error) {
            console.error('Error deleting comment:', error);
            closeDeleteModal();
        }
    };


    const toggleCommentLike = async (commentId) => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        try {
            const response = await api.post(`/bookReader/comments/${commentId}/like`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setComments((prev) => prev.map((comment) => {
                if (comment.comment_id === commentId) {
                    const newLikesCount = response.data.isLiked
                        ? parseInt(comment.likes_count, 10) + 1
                        : Math.max(0, parseInt(comment.likes_count, 10) - 1);
                    return {
                        ...comment,
                        is_liked_by_user: response.data.isLiked,
                        likes_count: newLikesCount,
                    };
                }
                return comment;
            }));
        } catch (error) {
            console.error('Error liking the comment:', error);
        }
    };

    const startReply = (commentId) => {
        setReplyTo(replyTo === commentId ? null : commentId); // Toggle reply form
    };

    const changeChapter = (newChapterId) => {
        if (newChapterId) {
            navigate(`/read/${bookId}/${newChapterId}`);
        }
    };

    const preventCopying = (event) => {
        if (event.type === 'contextmenu') {
            event.preventDefault();
        }
        if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === 'c' || event.key === 'x' || event.key === 'u' || event.key === 'a')
        ) {
            event.preventDefault();
        }
    };

    useEffect(() => {
        document.addEventListener('contextmenu', preventCopying);
        document.addEventListener('keydown', preventCopying);
        return () => {
            document.removeEventListener('contextmenu', preventCopying);
            document.removeEventListener('keydown', preventCopying);
        };
    }, []);

    // --- YENİ YORUM OLUŞTURMA FONKSİYONU ---
    const renderComments = (parentId = null) => {
        return comments
            .filter((comment) => comment.parent_comment_id === parentId)
            .map((comment) => (
                <div key={comment.comment_id} className="comment-thread">
                    <div className="comment-card">
                        <img
                            src={getProfileImageUrl(comment.profile_image_url)}
                            alt="Profil"
                            className="comment-avatar"
                        />
                        <div className="comment-main">
                            <div className="comment-header">
                                <span className="comment-username">{comment.username}</span>
                            </div>
                            <p className="comment-text">{comment.content}</p>
                            <div className="comment-footer">
                                <button className="comment-action-button" onClick={() => toggleCommentLike(comment.comment_id)}>
                                    <Heart
                                        size={14}
                                        fill={comment.is_liked_by_user ? '#310034' : 'none'}
                                        color={comment.is_liked_by_user ? '#310034' : '#666'}
                                    />
                                    <span>{comment.likes_count}</span>
                                </button>
                                {parentId === null && (
                                    <button className="comment-action-button" onClick={() => startReply(comment.comment_id)}>
                                        <CornerDownRight size={14} />
                                        <span>Yanıtla</span>
                                    </button>
                                )}
                                {comment.user_id === currentUserId && (
                                    <button className="comment-action-button delete" onClick={() => openDeleteModal(comment.comment_id)}>
                                        <Trash2 size={14} />
                                        <span>Sil</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    {replyTo === comment.comment_id && parentId === null && (
                        <CommentForm
                            placeholder="Yanıtınızı yazın..."
                            buttonText={<Send size={20} />}
                            onSubmit={(content) => addComment(content, comment.comment_id)}
                            isReply={true}
                        />
                    )}
                    <div className="comment-replies">
                        {renderComments(comment.comment_id)}
                    </div>
                </div>
            ));
    };

    // Yorum yazma formu için ayrı bir bileşen
    const CommentForm = ({ placeholder, buttonText, onSubmit, isReply = false }) => {
        const [content, setContent] = useState('');

        const handleSubmit = (e) => {
            e.preventDefault();
            onSubmit(content);
            setContent('');
        };

        return (
            <form onSubmit={handleSubmit} className={`comment-form ${isReply ? 'reply-form' : ''}`}>
                <input
                    type="text"
                    placeholder={placeholder}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                />
                <button type="submit">{buttonText}</button>
            </form>
        );
    };

    if (loading) {
        return (
            <div className="book-reader">
                <div className="loading-spinner">Yükleniyor...</div>
            </div>
        );
    }

    if (!book || !currentChapter) {
        return (
            <div className="book-reader">
                <div className="error-message">Kitap bilgileri yüklenemedi. Lütfen daha sonra tekrar deneyin.</div>
            </div>
        );
    }

    return (
        <div className="book-reader">
            <div className="header-section">
                <div className="button-strip">
                    <div className="book-header" onClick={toggleChapters}>
                        <div className="book-details">
                            <span className="book-title">{book.title}</span>
                            <span className="book-author">{book.author_name || 'Bilinmeyen Yazar'} tarafından</span>
                        </div>
                        <span className="dropdown-arrow">{showChapters ? '▲' : '▼'}</span>
                    </div>
                    <div className="buttons">
                        <button className="back-to-book" onClick={() => navigate(`/book/${bookId}`)}>
                            Kitaba Geri Dön
                        </button>
                        <button
                            className={`like-button-header ${likedChapters[currentChapter.chapter_id] ? 'liked' : ''}`}
                            onClick={handleLike}
                        >
                            <Heart
                                size={20}
                                fill={likedChapters[currentChapter.chapter_id] ? '#310034' : 'none'}
                                color={likedChapters[currentChapter.chapter_id] ? '#310034' : '#310034'}
                            />
                            <span>{likedChapters[currentChapter.chapter_id] ? 'Beğenildi' : 'Beğen'}</span>
                        </button>
                    </div>
                </div>
                {showChapters && (
                    <div className="chapter-list">
                        {book.chapters.map((chapter) => (
                            <div
                                key={chapter.chapter_id}
                                className={`chapter-item ${chapter.chapter_id === parseInt(chapterId, 10) ? 'active' : ''}`}
                                onClick={() => changeChapter(chapter.chapter_id)}
                            >
                                <span>{chapter.title}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="read-main">
                <div className="story-info">
                    <h1>{currentChapter.title}</h1>
                    <div className="story-stats">
                        <span><Eye size={18} /> {views} okuma</span>
                        <span><Heart size={18} fill={likedChapters[currentChapter.chapter_id] ? '#310034' : 'none'} /> {currentChapter.likes} beğeni</span>
                        <span><MessageCircle size={18} /> {comments.length} yorum</span>
                    </div>
                </div>
                <div
                    className="story-content"
                    style={{ userSelect: 'none' }}
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(currentChapter.content || '')
                            .split('\n')
                            .filter(p => p.trim().length > 0)
                            .map(paragraph => `<p>${paragraph}</p>`)
                            .join(''),
                    }}
                />
                <div className="chapter-navigation">
                    <button onClick={() => changeChapter(book.chapters[currentChapter.chapter_number - 2]?.chapter_id)} disabled={currentChapter.chapter_number === 1}>
                        <ArrowLeft size={20} /> Önceki Bölüm
                    </button>
                    <button onClick={() => changeChapter(book.chapters[currentChapter.chapter_number]?.chapter_id)} disabled={currentChapter.chapter_number === book.chapters.length}>
                        Sonraki Bölüm <ArrowRight size={20} />
                    </button>
                </div>
                {/* --- YENİ YORUM BÖLÜMÜ --- */}
                <div className="comment-section">
                    <h3>Yorumlar ({comments.length})</h3>
                    <CommentForm
                        placeholder="Bir yorum yazın..."
                        buttonText={<Send size={20} color="#fff" />}
                        onSubmit={(content) => addComment(content, null)}
                    />
                    <div className="comments-list">
                        {comments.length > 0 ? (
                            renderComments()
                        ) : (
                            <div className="no-comments">Henüz hiç yorum yapılmamış. İlk yorumu sen yap!</div>
                        )}
                    </div>
                </div>
            </div>

            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Yorumu Sil</h3>
                        <p>Bu yorumu ve altındaki tüm yanıtları silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
                        <div className="modal-actions">
                            <button className="modal-cancel" onClick={closeDeleteModal}>İptal</button>
                            <button className="modal-confirm" onClick={handleConfirmDelete}>Sil</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookReader;
